/**
 * Membership Status Constants
 *
 * Standard membership statuses used across the application.
 * These values match the Drupal membership taxonomy/field values.
 */

export const MEMBERSHIP_STATUS = {
  ACTIVE: 'Active member',
  ASSOCIATE: 'Associate member',
  AFFILIATE: 'Affiliate member',
  SUSPENDED: 'Suspended',
  RESIGNED: 'Resigned',
  EXCLUDED: 'Excluded',
} as const;

export type MembershipStatus = (typeof MEMBERSHIP_STATUS)[keyof typeof MEMBERSHIP_STATUS];

/**
 * Check if user has an active membership (any type of member)
 */
export function hasActiveMembership(membership: string | null | undefined): boolean {
  if (!membership) return false;
  return (
    membership === MEMBERSHIP_STATUS.ACTIVE ||
    membership === MEMBERSHIP_STATUS.ASSOCIATE ||
    membership === MEMBERSHIP_STATUS.AFFILIATE
  );
}

/**
 * Client-side function to check if user has one of the required memberships
 * Uses membership data from context (e.g., UserContext)
 *
 * @param membership - Current membership label from context
 * @param requiredMemberships - Array of required membership statuses
 * @returns True if user has one of the required memberships
 *
 * @example
 * ```typescript
 * // In a Client Component
 * const { membership } = useUser();
 * const hasAccess = hasRequiredMembershipClient(
 *   membership,
 *   [MEMBERSHIP_STATUS.ACTIVE, MEMBERSHIP_STATUS.ASSOCIATE]
 * );
 * ```
 */
export function hasRequiredMembershipClient(
  membership: string | null | undefined,
  requiredMemberships: MembershipStatus[]
): boolean {
  if (!membership || !requiredMemberships || requiredMemberships.length === 0) {
    return false;
  }
  return requiredMemberships.some(required => membership === required);
}

/**
 * Check if user is suspended
 */
export function isSuspendedMember(membership: string | null | undefined): boolean {
  return membership === MEMBERSHIP_STATUS.SUSPENDED;
}

/**
 * Check if user has no access (suspended, resigned, or excluded)
 */
export function hasNoAccess(membership: string | null | undefined): boolean {
  if (!membership) return false;
  return (
    membership === MEMBERSHIP_STATUS.SUSPENDED ||
    membership === MEMBERSHIP_STATUS.RESIGNED ||
    membership === MEMBERSHIP_STATUS.EXCLUDED
  );
}

/**
 * Get display colour for membership
 */
export function getMembershipColour(membership: string | null | undefined): string {
  if (!membership) return 'text-gray-500';

  switch (membership) {
    case MEMBERSHIP_STATUS.ACTIVE:
    case MEMBERSHIP_STATUS.ASSOCIATE:
    case MEMBERSHIP_STATUS.AFFILIATE:
      return 'text-green-600';
    case MEMBERSHIP_STATUS.SUSPENDED:
      return 'text-orange-600';
    case MEMBERSHIP_STATUS.RESIGNED:
    case MEMBERSHIP_STATUS.EXCLUDED:
      return 'text-red-600';
    default:
      return 'text-gray-500';
  }
}

/**
 * Mapping from membership labels to taxonomy machine names
 * Used for Drupal API queries that require taxonomy term names
 */
export const MEMBERSHIP_TAXONOMY_NAMES: Record<string, string> = {
  'Active member': 'active_member',
  'Associate member': 'associate_member',
  'Affiliate member': 'affiliate_member',
  Suspended: 'suspended',
  Resigned: 'resigned',
  Excluded: 'excluded',
} as const;

/**
 * Get the taxonomy machine name for a membership label
 *
 * @param membership - Membership label (e.g., "Active member")
 * @returns Taxonomy machine name (e.g., "active_member") or null
 *
 * @example
 * ```typescript
 * getMembershipTaxonomyName('Active member'); // Returns: 'active_member'
 * getMembershipTaxonomyName('Unknown'); // Returns: null
 * ```
 */
export function getMembershipTaxonomyName(membership: string | null | undefined): string | null {
  if (!membership) return null;
  return MEMBERSHIP_TAXONOMY_NAMES[membership] || null;
}

/**
 * Check if a membership string matches any membership term in an array
 *
 * @param membershipTerms - Array of membership taxonomy terms (with `name` property)
 * @param membership - Membership string to match (e.g., "Active member")
 * @returns True if membership matches any term's name
 *
 * @example
 * ```typescript
 * const terms = [{ name: 'Active member' }, { name: 'Associate member' }];
 * hasMatchingMembership(terms, 'Active member'); // Returns: true
 * hasMatchingMembership(terms, 'Public'); // Returns: false
 * hasMatchingMembership(terms, null); // Returns: false
 * ```
 */
export function hasMatchingMembership(
  membershipTerms: unknown,
  membership: string | null | undefined
): boolean {
  if (!membership || !membershipTerms) {
    return false;
  }

  if (!Array.isArray(membershipTerms) || membershipTerms.length === 0) {
    return false;
  }

  return membershipTerms.some(term => {
    if (!term || typeof term !== 'object') return false;
    const termObj = term as { name?: string | null };
    return termObj.name === membership;
  });
}

